import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import GitPullRequestIcon from "lucide-react/dist/esm/icons/git-pull-request";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { FunnelSankey } from "@/components/chart/funnel-sankey";
import { ScenarioFlowCanvas } from "@/components/flow/flow-canvas";
import { PageBody, Panel } from "@/components/shell/page-frame";
import { TabNav } from "@/components/shell/tab-nav";
import { StatusBadge } from "@/components/status/status-badge";
import { Button } from "@/components/ui/button";
import { SCENARIO_STATE_LABEL, SCENARIO_STATE_TONE } from "@/features/operations/types";
import {
  getScenario,
  getScenarioDefinition,
  type ScenarioStep,
  STEP_KIND_LABEL
} from "@/features/scenarios/definition";
import { formatInteger, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";

const builderSearchSchema = z.object({
  tab: z.enum(["flow", "funnel", "improve"]).catch("flow").default("flow")
});

export const Route = createFileRoute("/scenarios/$scenarioId")({
  validateSearch: builderSearchSchema,
  loader: ({ params }) => {
    const scenario = getScenario(params.scenarioId);
    const definition = getScenarioDefinition(params.scenarioId);
    if (!scenario || !definition) throw notFound();
    return { scenario, definition };
  },
  component: ScenarioBuilderPage
});

/**
 * シナリオビルダー。トリガー × 条件 × アクションの編集がシステムの本質。
 * 24シナリオすべてこの 1 画面で扱う。
 * - flow: 構成（トリガー→条件→アクション）
 * - funnel: どのステップで離脱しているか
 * - improve: 配信の自動改善(4-1) と シナリオのAI改善提案(4-3)。人が承認する。
 */
function ScenarioBuilderPage() {
  const { scenario, definition } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const improveCount = (definition.deliverySuggestion ? 1 : 0) + (definition.improvement ? 1 : 0);

  return (
    <>
      {/* ビルダー専用ヘッダー。編集画面なので概況系の PageHeader とは別に組む。 */}
      <div className="sticky top-(--topbar-h) z-30 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="シナリオ一覧へ戻る"
          render={<Link to="/scenarios" />}
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-lg font-semibold tracking-tight text-fg">{scenario.name}</h1>
          <StatusBadge tone={SCENARIO_STATE_TONE[scenario.state]}>
            {SCENARIO_STATE_LABEL[scenario.state]}
          </StatusBadge>
        </div>
        <span className="hidden text-xs text-fg-subtle lg:inline">
          {scenario.category} · {scenario.trigger}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm">
            テスト配信
          </Button>
          <Button type="button" size="sm" onClick={() => toast.success("シナリオを保存しました")}>
            保存
          </Button>
        </div>
      </div>

      <TabNav
        className="sticky top-[calc(var(--topbar-h)+var(--pagehead-h))] z-20 bg-surface"
        value={search.tab}
        onChange={(tab) => navigate({ search: { tab } })}
        items={[
          { value: "flow", label: "構成" },
          { value: "funnel", label: "ファネル" },
          { value: "improve", label: "AI改善提案", count: improveCount }
        ]}
      />

      {search.tab === "flow" ? (
        // ノード＋エッジのキャンバスは全幅・高さいっぱいで表示する
        <div
          className="border-t border-line"
          style={{ height: "calc(100dvh - var(--topbar-h) - var(--pagehead-h) - 37px)" }}
        >
          <ScenarioFlowCanvas definition={definition} />
        </div>
      ) : (
        <PageBody density="dense" className="mx-auto w-full max-w-220">
          {search.tab === "funnel" ? <FunnelView steps={definition.steps} /> : null}
          {search.tab === "improve" ? (
            <ImproveView
              delivery={definition.deliverySuggestion}
              improvement={definition.improvement}
            />
          ) : null}
        </PageBody>
      )}
    </>
  );
}

function FunnelView({ steps }: { steps: ScenarioStep[] }) {
  const start = steps[0]?.entered ?? 1;

  return (
    <div className="grid gap-4">
      <Panel title="ファネルフロー" description="どこで顧客が流れ、どこで離脱しているか">
        <FunnelSankey steps={steps} />
      </Panel>
      <Panel title="ステップ別詳細" description="各ステップの通過と離脱">
        <div className="grid gap-2">
          {steps.map((step) => {
            const share = (step.advanced / start) * 100;
            const dropped = step.entered - step.advanced;
            const dropRate = step.entered > 0 ? (dropped / step.entered) * 100 : 0;
            const highDrop = dropRate >= 40 && step.kind === "action";

            return (
              <div key={step.id} className="grid gap-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-fg">
                    <span className="mr-1.5 text-2xs text-fg-subtle">
                      {STEP_KIND_LABEL[step.kind]}
                    </span>
                    {step.title}
                  </span>
                  <span className="num shrink-0 text-fg-muted">
                    {formatInteger(step.advanced)}
                    <span className="ml-1 text-fg-subtle">({formatPercent(share)})</span>
                  </span>
                </div>
                <div className="relative h-6 overflow-hidden rounded-sm bg-surface-sunken">
                  <div
                    className="h-full rounded-sm bg-brand/70"
                    style={{ width: `${Math.max(share, 1)}%` }}
                  />
                </div>
                {dropped > 0 ? (
                  <p
                    className={cn(
                      "text-2xs",
                      highDrop ? "font-semibold text-critical-fg" : "text-fg-subtle"
                    )}
                  >
                    ↓ {formatInteger(dropped)}人が離脱（{formatPercent(dropRate)}）
                    {highDrop ? " ── 離脱が大きいステップ" : ""}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function ImproveView({
  delivery,
  improvement
}: {
  delivery: NonNullable<ReturnType<typeof getScenarioDefinition>>["deliverySuggestion"];
  improvement: NonNullable<ReturnType<typeof getScenarioDefinition>>["improvement"];
}) {
  const [appliedDelivery, setAppliedDelivery] = useState(false);
  const [mergedPR, setMergedPR] = useState(false);

  if (!delivery && !improvement) {
    return (
      <Panel title="AI改善提案">
        <p className="py-8 text-center text-sm text-fg-muted">
          現在このシナリオへの改善提案はありません。週次レビューで新しい提案が生成されます。
        </p>
      </Panel>
    );
  }

  return (
    <div className="grid gap-4">
      {/* 配信の自動改善(4-1): 勝ちパターンを提示 → 人が承認して適用 */}
      {delivery ? (
        <Panel
          title="配信の自動改善"
          count={appliedDelivery ? "適用済" : "承認待ち"}
          description="過去の配信結果から抽出した勝ちパターン"
        >
          <div className="rounded-lg border border-info-line bg-info-subtle p-3">
            <p className="flex items-center gap-1.5 text-2xs font-semibold text-info-fg">
              <SparklesIcon className="size-3" aria-hidden="true" />
              {delivery.metric}を +{formatPercent(delivery.expectedLift)} 見込み
            </p>
            <p className="mt-1 text-sm text-fg">{delivery.rationale}</p>
          </div>
          <div className="mt-3 grid gap-2">
            <DiffLine kind="before" text={delivery.before} />
            <DiffLine kind="after" text={delivery.after} />
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            {appliedDelivery ? (
              <span className="flex items-center gap-1 text-sm text-success-fg">
                <CheckIcon className="size-4" aria-hidden="true" />
                適用しました
              </span>
            ) : (
              <>
                <Button type="button" size="sm" variant="ghost">
                  却下
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setAppliedDelivery(true);
                    toast.success("改善案を適用しました", {
                      action: { label: "元に戻す", onClick: () => setAppliedDelivery(false) }
                    });
                  }}
                >
                  承認して適用
                </Button>
              </>
            )}
          </div>
        </Panel>
      ) : null}

      {/* シナリオのAI改善提案(4-3): 変更を PR 形式の差分で提示 → 人がレビュー・承認 */}
      {improvement ? (
        <Panel
          title="シナリオのAI改善提案"
          count={mergedPR ? "反映済" : "レビュー待ち"}
          description="週次レビューで生成された変更提案（PR形式）"
        >
          <p className="mb-3 flex items-start gap-1.5 text-sm text-fg">
            <GitPullRequestIcon
              className="mt-0.5 size-4 shrink-0 text-fg-subtle"
              aria-hidden="true"
            />
            {improvement.summary}
          </p>
          {improvement.hunks.map((hunk) => (
            <div key={hunk.stepTitle} className="mb-2 rounded-lg border border-line">
              <p className="border-b border-line bg-surface-sunken px-3 py-1.5 text-xs font-medium text-fg">
                {hunk.stepTitle}
              </p>
              <div className="grid gap-1 p-2">
                <DiffLine kind="before" text={hunk.before} />
                <DiffLine kind="after" text={hunk.after} />
              </div>
            </div>
          ))}
          <div className="mt-2 flex items-center justify-end gap-2">
            {mergedPR ? (
              <span className="flex items-center gap-1 text-sm text-success-fg">
                <CheckIcon className="size-4" aria-hidden="true" />
                反映しました
              </span>
            ) : (
              <>
                <Button type="button" size="sm" variant="ghost">
                  却下
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setMergedPR(true);
                    toast.success("変更を反映しました", {
                      action: { label: "元に戻す", onClick: () => setMergedPR(false) }
                    });
                  }}
                >
                  承認して反映
                </Button>
              </>
            )}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function DiffLine({ kind, text }: { kind: "before" | "after"; text: string }) {
  return (
    <div
      className={cn(
        "flex gap-2 rounded-sm px-2 py-1 text-sm",
        kind === "before"
          ? "bg-critical-subtle text-critical-fg"
          : "bg-success-subtle text-success-fg"
      )}
    >
      <span aria-hidden="true" className="select-none font-semibold">
        {kind === "before" ? "−" : "+"}
      </span>
      <span className="min-w-0 flex-1 text-fg">{text}</span>
    </div>
  );
}
